import React, { createContext, useContext, useEffect, useReducer, useRef } from 'react';
import { RuntimeTask } from '../types/runtime';
import { runtimeAPI } from '../services/api';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';

type State = {
  tasks: RuntimeTask[];
  expanded: boolean;
  unread: number;
};

type Action =
  | { type: 'SET_TASKS'; tasks: RuntimeTask[] }
  | { type: 'UPSERT_TASK'; task: RuntimeTask }
  | { type: 'REMOVE_TASK'; id: string }
  | { type: 'SET_EXPANDED'; expanded: boolean }
  | { type: 'CLEAR_UNREAD' }
  | { type: 'INCREMENT_UNREAD' };

const initial: State = { tasks: [], expanded: (localStorage.getItem('runtimeDock.expanded') === 'true'), unread: 0 };

function reducer(s: State, a: Action): State {
  switch (a.type) {
    case 'SET_TASKS':
      return { ...s, tasks: a.tasks };
    case 'UPSERT_TASK': {
      const idx = s.tasks.findIndex(t => t.id === a.task.id);
      if (idx === -1) return { ...s, tasks: [a.task, ...s.tasks] };
      const copy = s.tasks.slice();
      copy[idx] = { ...copy[idx], ...a.task };
      return { ...s, tasks: copy };
    }
    case 'REMOVE_TASK':
      return { ...s, tasks: s.tasks.filter(t => t.id !== a.id) };
    case 'SET_EXPANDED':
      localStorage.setItem('runtimeDock.expanded', String(a.expanded));
      return { ...s, expanded: a.expanded };
    case 'CLEAR_UNREAD':
      return { ...s, unread: 0 };
    case 'INCREMENT_UNREAD':
      return { ...s, unread: s.unread + 1 };
    default:
      return s;
  }
}

const RuntimeContext = createContext<{
  state: State;
  dispatch: React.Dispatch<Action>;
  stopTask: (id: string) => Promise<void>;
} | null>(null);

export function RuntimeProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initial);
  const wsRef = useRef<any /* Socket.IO socket */ | null>(null);
  const pollRef = useRef<any>(null);
  const reconnectRef = useRef(0);
  const expandedRef = useRef<boolean>(initial.expanded);

  useEffect(() => {
    let mounted = true;
    const fetchInitial = async () => {
      // Only fetch tasks when the runtime dock is expanded (visible to the user)
      if (!expandedRef.current) return;
      // RuntimeProvider is mounted app-wide (index.js), outside the router —
      // "expanded" persists in localStorage across logout, so without this
      // guard a stale expanded dock keeps fetching on the public Landing/
      // Login page with no session to fetch for.
      if (!localStorage.getItem('authToken')) return;
      try {
        const tasks = await runtimeAPI.getTasks();
        if (!mounted) return;
        dispatch({ type: 'SET_TASKS', tasks });
      } catch (e) {
        // ignore
      }
    };
    // call initial fetch only if expanded
    fetchInitial();

    const refreshTasks = async () => {
      // avoid background refresh when dock is collapsed
      if (!expandedRef.current) return;
      if (!localStorage.getItem('authToken')) return;
      try {
        const tasks = await runtimeAPI.getTasks();
        dispatch({ type: 'SET_TASKS', tasks });
      } catch (e) {
        // ignore
      }
    };

    // Connect using Socket.IO (server uses socket.io)
    function connect() {
      try {
        const base = process.env.REACT_APP_WS_URL || window.location.origin;
        const socket = io(base, { path: '/socket.io' });
        wsRef.current = socket;
        socket.on('connect', () => {
          reconnectRef.current = 0;
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        });

        // Map server runtime events to local dispatches
        socket.on('agent.started', (p: any) => {
          dispatch({ type: 'UPSERT_TASK', task: { id: p.id, agentId: p.agentId, agentName: p.agentName || p.agentId || 'Unknown', status: 'running', progress: p.progress ?? 0, currentStep: p.currentStep, startedAt: p.startedAt, model: p.model, tool: p.tool } });
          if (!expandedRef.current) dispatch({ type: 'INCREMENT_UNREAD' });
          try { window.dispatchEvent(new CustomEvent('lyra:run-start', { detail: p })); } catch (_) {}
        });
        socket.on('agent.progress', (p: any) => dispatch({ type: 'UPSERT_TASK', task: { id: p.id, progress: p.progress ?? 0, currentStep: p.currentStep } as any }));
        socket.on('agent.completed', (p: any) => dispatch({ type: 'UPSERT_TASK', task: { id: p.id, status: 'completed', progress: 100 } as any }));
        socket.on('agent.failed', (p: any) => {
          dispatch({ type: 'UPSERT_TASK', task: { id: p.id, status: 'failed' } as any });
          toast.error(`${p.agentName || 'Agent'} failed`);
          try { window.dispatchEvent(new CustomEvent('lyra:run-end', { detail: p })); } catch (_) {}
        });
        socket.on('agent.cancelled', (p: any) => {
          dispatch({ type: 'UPSERT_TASK', task: { id: p.id, status: 'cancelled' } as any });
          try { window.dispatchEvent(new CustomEvent('lyra:run-end', { detail: p })); } catch (_) {}
        });
        socket.on('agent.completed', (p: any) => { dispatch({ type: 'UPSERT_TASK', task: { id: p.id, status: 'completed', progress: 100 } as any }); try { window.dispatchEvent(new CustomEvent('lyra:run-end', { detail: p })); } catch (_) {} });

        socket.on('disconnect', () => {
          // start polling fallback only if dock is visible
          if (!pollRef.current && expandedRef.current) {
            pollRef.current = setInterval(async () => {
              if (!localStorage.getItem('authToken')) return;
              try {
                const tasks = await runtimeAPI.getTasks();
                dispatch({ type: 'SET_TASKS', tasks });
              } catch (e) {}
            }, 5000);
          }
        });

        socket.on('connect_error', () => {
          // fallback to polling until socket reconnects (only when expanded)
          if (!pollRef.current && expandedRef.current) {
            pollRef.current = setInterval(async () => {
              if (!localStorage.getItem('authToken')) return;
              try {
                const tasks = await runtimeAPI.getTasks();
                dispatch({ type: 'SET_TASKS', tasks });
              } catch (e) {}
            }, 5000);
          }
        });
      } catch (e) {
        // fallback to polling
        if (!pollRef.current) {
          pollRef.current = setInterval(async () => {
            if (!localStorage.getItem('authToken')) return;
            try {
              const tasks = await runtimeAPI.getTasks();
              dispatch({ type: 'SET_TASKS', tasks });
            } catch (e) {}
          }, 5000);
        }
      }
    }
    connect();

    // Refresh tasks when local run lifecycle events fire (mission control / UI)
    const onRunChange = () => { if (expandedRef.current) refreshTasks(); };
    const onRunStep = () => { if (expandedRef.current) refreshTasks(); };
    const onAgentScheduled = () => { if (expandedRef.current) refreshTasks(); };
    window.addEventListener('lyra:run-start', onRunChange);
    window.addEventListener('lyra:run-end', onRunChange);
    window.addEventListener('lyra:run-step', onRunStep);
    window.addEventListener('lyra:agent-scheduled', onAgentScheduled);

    return () => {
      mounted = false;
      if (wsRef.current) wsRef.current.close();
      if (pollRef.current) clearInterval(pollRef.current);
      window.removeEventListener('lyra:run-start', onRunChange);
      window.removeEventListener('lyra:run-end', onRunChange);
      window.removeEventListener('lyra:run-step', onRunStep);
      window.removeEventListener('lyra:agent-scheduled', onAgentScheduled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the user expands the runtime dock, fetch tasks once and enable polling if socket disconnected
  useEffect(() => {
    if (state.expanded && localStorage.getItem('authToken')) {
      // fetch latest tasks when user opens the dock
      (async () => {
        try {
          const tasks = await runtimeAPI.getTasks();
          dispatch({ type: 'SET_TASKS', tasks });
        } catch (e) {}
      })();
      // if socket is disconnected and no poll running, start polling
      if (!wsRef.current && !pollRef.current) {
        pollRef.current = setInterval(async () => {
          if (!localStorage.getItem('authToken')) return;
          try {
            const tasks = await runtimeAPI.getTasks();
            dispatch({ type: 'SET_TASKS', tasks });
          } catch (e) {}
        }, 5000);
      }
    } else {
      // when collapsed, stop any polling
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
    // keep expandedRef in sync
    expandedRef.current = state.expanded;
  }, [state.expanded]);

  // detect all-complete -> toast
  const prevRunningRef = useRef(0);
  useEffect(() => {
    const running = state.tasks.filter(t => t.status === 'running' || t.status === 'queued').length;
    if (prevRunningRef.current > 0 && running === 0) {
      toast.success('All runtime tasks completed');
    }
    prevRunningRef.current = running;
  }, [state.tasks]);

  // keep ref up-to-date for websocket handlers
  useEffect(() => {
    expandedRef.current = state.expanded;
  }, [state.expanded]);

  const stopTask = async (id: string) => {
    // optimistic
    const existing = state.tasks.find(t => t.id === id);
    if (existing) dispatch({ type: 'UPSERT_TASK', task: { ...existing, status: 'cancelled' } });
    try {
      await runtimeAPI.stopTask(id);
    } catch (e: any) {
      // revert
      if (existing) dispatch({ type: 'UPSERT_TASK', task: existing });
      toast.error('Failed to stop task');
    }
  };

  return (
    <RuntimeContext.Provider value={{ state, dispatch, stopTask }}>
      {children}
    </RuntimeContext.Provider>
  );
}

export function useRuntime() {
  const ctx = useContext(RuntimeContext);
  if (!ctx) throw new Error('useRuntime must be used within RuntimeProvider');
  return ctx;
}
